import { AxiosError } from 'axios';
import { ErrorObject } from '@/api/common.types';
import { KycErrorCode } from '@/constants/kycErrorCodes';

/**
 * Classifies a failed create-buy-transaction response so the buy flow can react
 * appropriately instead of always showing the generic "high demand" popup.
 *
 * The backend returns a machine-readable `code` (ApiErrorResponse.code) carrying the
 * KycVerificationErrorCode enum name — see @/constants/kycErrorCodes.
 */

// KYC/biometric failures where the user can recover by (re)completing verification.
const KYC_REVERIFY_CODES: ReadonlySet<string> = new Set([
  KycErrorCode.CheckIdNotPresent,
  KycErrorCode.CodeNotMatched,
  KycErrorCode.CodeExpired,
  KycErrorCode.BuyCryptoNotFound,
  KycErrorCode.StatusError,
  KycErrorCode.ActionError,
  KycErrorCode.InspectionIdNotPresent,
  KycErrorCode.MethodNotAllowed,
  KycErrorCode.UserError,
  KycErrorCode.KycStatusError,
]);

export type BuyTxnErrorKind = 'reverify' | 'attemptsExhausted' | 'transient';

export function getBuyTxnErrorCode(error: unknown): string | undefined {
  return (error as AxiosError<ErrorObject> | null | undefined)?.response?.data?.code;
}

export function classifyBuyTxnError(error: unknown): BuyTxnErrorKind {
  const code = getBuyTxnErrorCode(error);
  if (!code) return 'transient';
  if (code === KycErrorCode.AttemptLimit) return 'attemptsExhausted';
  if (KYC_REVERIFY_CODES.has(code)) return 'reverify';
  // Anything else (5xx, gateway, network/timeout, unknown) is genuinely transient.
  return 'transient';
}
